import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getNextVideoStyleObject } from './nextVideoBigSize.style';
import { updateShowUpNext } from '../../store/discoveryPlugin.actions';
import { mediaDataSelector } from '../../../../common/store/selectors/mediaData.selector';
import { Icon } from '../../../../common/components/icon/icon';
import { COMMON_ICON_TYPES } from '../../../../common/components/icon/iconMapper';
import { getCloseId, getDurationId, getPlayId, getThumbnailId } from '../../htmlElements/htmlElementsIdGetter';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { getTimePresentation, isEmpty } from '../../../../common/utils/utils';
import { CountdownLoader } from '../countdownLoader/countdownLoader';
import { Title } from '../title/title';

const CLOSE_ARIA_LABEL = 'Close up next';
const PLAY_ARIA_LABEL = 'Play next video';

interface NextVideoComponentVariablesProps {
    setIsClosedUpNext: (isClosedUpNext: boolean) => void;
    countdownTime: number;
    onClickNextVideo: () => void;
}

export const NextVideoBigSize: FunctionComponent<NextVideoComponentVariablesProps> = props => {
  const {
    setIsClosedUpNext,
    countdownTime,
    onClickNextVideo,
  } = props;

  const dispatch = useDispatch();
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const videoList = useSelector(mediaDataSelector.videoList);
  const activeVideoIndex = useSelector(mediaDataSelector.activeVideoIndex);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);

  const [nextVideoTitle, setNextVideoTitle] = useState('');
  const [nextVideoThumbnail, setNextVideoThumbnail] = useState('');
  const [nextVideoDuration, setNextVideoDuration] = useState(0);

  const isLastVideo = activeVideoIndex === videoList.length - 1;
  const isEndState = videoList.length === 1 && videoTagStatus === 'complete';

  const styles = useStyles(getNextVideoStyleObject, []);
  const { nextVideoContainerStyle, nextVideoStyle, thumbnailStyle, thumbnailWrapperStyle, blurThumbnailStyle, closeIconStyle, playBlurIconStyle, durationStyle } = styles;

  useEffect(() => {
    const nextVideoIndex = isLastVideo ? 0 : activeVideoIndex + 1;
    if (!isEmpty(videoList)) {
      const { duration, thumbnail, title } = videoList[nextVideoIndex];
      setNextVideoTitle(title);
      setNextVideoThumbnail(thumbnail);
      setNextVideoDuration(duration);
    }
  }, [activeVideoIndex, isLastVideo, videoList]);

  const onClickCloseIcon = (): void => {
    dispatch(updateShowUpNext(false));
    setIsClosedUpNext(true);
  };

  return (
    <div style={nextVideoContainerStyle}>
      <div style={nextVideoStyle}>
        <div id={getThumbnailId(playerUniqueId)} onClick={onClickNextVideo} style={thumbnailWrapperStyle}>
          <img src={nextVideoThumbnail} alt="next video thumbnail" style={thumbnailStyle} />
          <div style={blurThumbnailStyle} />
        </div>
        { isEndState
          && (
            <Icon
              icon={COMMON_ICON_TYPES.CLOSE_FILL}
              style={closeIconStyle}
              onClick={onClickCloseIcon}
              id={getCloseId(playerUniqueId)}
              ariaLabel={CLOSE_ARIA_LABEL}
            />
          ) }
        <Icon
          icon={COMMON_ICON_TYPES.PLAY_BLUR}
          style={playBlurIconStyle}
          id={getPlayId(playerUniqueId)}
          onClick={onClickNextVideo}
          ariaLabel={PLAY_ARIA_LABEL}
        />
        <div id={getDurationId(playerUniqueId)} style={durationStyle} onClick={onClickNextVideo}>
          {getTimePresentation(Math.floor(nextVideoDuration))}
        </div>
        <CountdownLoader countdownTime={countdownTime} onClickNextVideo={onClickNextVideo} />
      </div>
      <Title playerUniqueId={playerUniqueId} nextVideoTitle={nextVideoTitle} />
    </div>
  );
};
